import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, CheckCircle, XCircle, Clock, Loader2, User, ArrowRight, RefreshCw } from "lucide-react";

const statusColors = {
  pending_admin: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-slate-100 text-slate-600",
};

export default function EmailChangeReview() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_admin");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/email-change-requests?status=${filter}`);
      setRequests(r.data.requests || []);
      setPendingCount(r.data.pending_count || 0);
    } catch { toast.error("Failed to load requests"); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [filter]);

  const handleReview = async (action) => {
    if (!selected) return;
    setProcessing(true);
    try {
      const r = await api.post(`/admin/email-change-requests/${selected.request_id}/review`, { action, notes });
      toast.success(r.data.message);
      setSelected(null); setNotes("");
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Review failed"); }
    setProcessing(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="email-change-review-page">
      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-7 h-7 text-court-orange" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Email Change Requests</h1>
        {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-800">{pendingCount} pending</Badge>}
      </div>
      <p className="text-muted-foreground mb-6">Review and approve user email address changes</p>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {["pending_admin", "approved", "rejected", "all"].map(f => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm"
            className={`rounded-full text-xs font-heading uppercase ${filter === f ? "bg-court-orange text-white" : ""}`}
            onClick={() => { setFilter(f); setSelected(null); }} data-testid={`filter-${f}`}>
            {f === "pending_admin" ? "Pending" : f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={fetch} className="ml-auto"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
          <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="font-heading text-muted-foreground uppercase">No {filter === "all" ? "" : filter.replace("_", " ")} requests</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="space-y-2">
            {requests.map(req => (
              <div key={req.request_id}
                className={`p-4 border-2 cursor-pointer transition-colors ${selected?.request_id === req.request_id ? "border-court-orange bg-orange-50/50" : "border-slate-100 hover:border-slate-200"}`}
                onClick={() => { setSelected(req); setNotes(""); }} data-testid={`ecr-${req.request_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-heading text-sm font-bold">{req.user_name || "Unknown"}</span>
                  </div>
                  <Badge className={statusColors[req.status] || "bg-slate-100"}>{req.status?.replace("_", " ")}</Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{req.current_email}</span>
                  <ArrowRight className="w-3 h-3 flex-shrink-0 text-court-orange" />
                  <span className="truncate font-medium text-foreground">{req.new_email}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{new Date(req.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>

          {/* Detail */}
          {selected && (
            <div className="border-2 border-slate-100 p-6 space-y-4 sticky top-6 h-fit" data-testid="ecr-detail-panel">
              <h3 className="font-heading text-lg font-bold uppercase">Review Request</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">User:</span><span className="font-medium">{selected.user_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Current Email:</span><span className="font-mono text-xs">{selected.current_email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">New Email:</span><span className="font-mono text-xs font-bold text-court-orange">{selected.new_email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">OTP Verified:</span>
                  {selected.otp_verified ? <Badge className="bg-green-100 text-green-800 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />Verified</Badge> : <Badge className="bg-red-100 text-red-800 text-[10px]">Not Verified</Badge>}
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Requested:</span><span className="text-xs">{new Date(selected.created_at).toLocaleString()}</span></div>
                {selected.verified_at && <div className="flex justify-between"><span className="text-muted-foreground">OTP Verified At:</span><span className="text-xs">{new Date(selected.verified_at).toLocaleString()}</span></div>}
              </div>

              {selected.status === "pending_admin" && (
                <>
                  <div>
                    <label className="text-xs font-heading uppercase text-muted-foreground">Admin Notes (optional)</label>
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." className="mt-1" data-testid="review-notes" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleReview("approve")} disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-heading uppercase text-xs" data-testid="approve-email-btn">
                      {processing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}Approve
                    </Button>
                    <Button onClick={() => handleReview("reject")} disabled={processing} variant="destructive"
                      className="flex-1 font-heading uppercase text-xs" data-testid="reject-email-btn">
                      {processing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}Reject
                    </Button>
                  </div>
                </>
              )}

              {selected.reviewed_at && (
                <div className="p-3 bg-slate-50 border rounded text-xs space-y-1">
                  <p><span className="text-muted-foreground">Reviewed by:</span> {selected.reviewer_name || "Admin"}</p>
                  <p><span className="text-muted-foreground">At:</span> {new Date(selected.reviewed_at).toLocaleString()}</p>
                  {selected.review_notes && <p><span className="text-muted-foreground">Notes:</span> {selected.review_notes}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
